import * as React from 'react';
import { getGetApiTenantsTenantSlugEntityQueryKey, useGetApiTenantsTenantSlugEntity } from '@cdf/api-client-react';
import { SearchIcon, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  authorizedAdministrationDestinations,
  availableEntityPages,
  searchAdministrationDestinations
} from '@/features/tenancy/administration-navigation';
import { hasTenantPermission } from '@/features/tenancy/permissions';
import { tenantPath } from '@/features/tenancy/urls';

export function AdministrationSearch({
  entitySlug,
  permissions,
  active
}: {
  entitySlug: string;
  permissions: readonly string[];
  active: boolean;
}) {
  const [query, setQuery] = React.useState('');
  const canRead = hasTenantPermission(permissions, 'tenant.settings.read');
  const entityQuery = useGetApiTenantsTenantSlugEntity(entitySlug, {
    query: { queryKey: getGetApiTenantsTenantSlugEntityQueryKey(entitySlug), enabled: canRead }
  });
  const destinations = authorizedAdministrationDestinations(permissions, {
    active,
    availablePages: availableEntityPages(entityQuery.data, active)
  });
  const matches = searchAdministrationDestinations(destinations, query);

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1 space-y-1.5">
          <label htmlFor="entity-administration-search" className="text-sm font-medium">Search settings</label>
          <div className="relative">
            <SearchIcon aria-hidden className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              id="entity-administration-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find an administration page or section"
              className="pl-9"
            />
          </div>
        </div>
        {query ? <Button type="button" variant="outline" onClick={() => setQuery('')}><XIcon aria-hidden className="size-4" /> Clear search</Button> : null}
      </div>
      {query.trim() ? (
        <div aria-live="polite" className="rounded-lg border bg-card p-2">
          {matches.length ? (
            <ul className="grid gap-1 sm:grid-cols-2">
              {matches.map((item) => (
                <li key={item.to}>
                  <Button asChild variant="ghost" className="h-auto w-full justify-start whitespace-normal px-3 py-2 text-left">
                    <Link to={tenantPath(entitySlug, item.to)} onClick={() => setQuery('')}>
                      <span><span className="block font-medium">{item.label}</span><span className="text-muted-foreground block text-xs font-normal">{item.description}</span></span>
                    </Link>
                  </Button>
                </li>
              ))}
            </ul>
          ) : <p className="text-muted-foreground px-3 py-4 text-sm">No administration settings match “{query}”.</p>}
        </div>
      ) : null}
    </div>
  );
}
